import { setPresenceTarget } from "../lib/presence";
import { PresenceAvatars } from "./PresenceAvatars";
// Loan drawer: every field with the same autosave as the grid, revenue explainer + what-if,
// alerts and the field-level history.
import * as Tabs from "@radix-ui/react-tabs";
import { useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Check, ClipboardList, ExternalLink, History, Trash2, X } from "lucide-react";
import { useEffect, useMemo, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import type { EditableField, FieldValue, HistoryEntry, Loan, LoanSet, Me } from "../lib/types";
import { findCachedLoan } from "../lib/loanCache";
import { useHistory, useMe, useUsers } from "../lib/queries";
import { useLoanActions } from "../lib/mutations";
import { cellStatus, useSaveState } from "../lib/saveState";
import { money, price3, pts3, signedMoney } from "../lib/format";
import { dateLong, dateShort, daysBetween, monthLabel, todayIn } from "../lib/dates";
import { FORECAST_LONG, LOCK_LABEL, OUTCOME_LABEL, stageTone } from "../lib/stages";
import { previewPrice } from "../lib/price";
import { revenue } from "../lib/blend";
import { num } from "../lib/decimal";
import { fieldLabel } from "../lib/fields";
import { parseAmount } from "../grid/columns";
import { useIsDark } from "../lib/prefs";
import { Button, cx, StagePill, TierChip } from "./ui";
import { BELOW_TOP_NAV } from "./AppShell";
import { hasCap } from "../lib/roles";
import { AssignedStaff } from "./AssignedStaff";
import { LoCardSection } from "./LoCardSection";
import { LoanSmartDropPanel } from "./SmartDropWidget";
import { LoanStepTracker } from "./StepSections";
import { previewOn } from "../lib/preview";

export function useCachedLoan(id: string | null): Loan | undefined {
  const qc = useQueryClient();
  const cache = qc.getQueryCache();
  const subscribe = useMemo(() => (fn: () => void) => cache.subscribe(fn), [cache]);
  return useSyncExternalStore(
    subscribe,
    () => (id ? findCachedLoan(qc, id) : undefined),
    () => undefined,
  );
}

export function LoanDrawer({ loanId, onClose }: { loanId: string; onClose: () => void }) {
  const loan = useCachedLoan(loanId);
  const me = useMe();
  const ref = useRef<HTMLDivElement>(null);
  const returnFocus = useRef<Element | null>(null);

  useEffect(() => {
    setPresenceTarget(loanId);
    returnFocus.current = document.activeElement;
    ref.current?.querySelector<HTMLElement>("[data-autofocus]")?.focus();
    return () => {
      setPresenceTarget(null);
      const el = returnFocus.current as HTMLElement | null;
      el?.focus?.();
    };
  }, [loanId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || e.defaultPrevented) return;
      const t = e.target as HTMLElement | null;
      if (t?.closest("#portal") || t?.closest("[cmdk-root]")) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <aside data-loan-id={loanId}
      ref={ref}
      role="dialog"
      aria-modal="false"
      aria-label={loan ? `Loan ${loan.file_name}` : "Loan"}
      onFocusCapture={(e) => {
        const field = (e.target as HTMLElement).closest<HTMLElement>("[data-presence-field]")?.dataset.presenceField;
        setPresenceTarget(loanId, loan?.can_edit ? field : undefined);
      }}
      className={cx("drawer-in fixed right-0 bottom-0 z-40 flex w-full flex-col border-l border-line bg-raised shadow-[var(--shadow-3)] sm:w-[520px] [[data-density=compact]_&]:sm:w-[460px]", BELOW_TOP_NAV)}
    >
      {loan ? (
        <DrawerBody loan={loan} me={me} onClose={onClose} />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center text-ink-2">
          <div>This loan isn't loaded here, or it was deleted.</div>
          <Button onClick={onClose}>Close</Button>
        </div>
      )}
    </aside>
  );
}

function DrawerBody({ loan, me, onClose }: { loan: Loan; me: Me; onClose: () => void }) {
  const navigate = useNavigate();
  const actions = useLoanActions();
  const stages = me.stages;
  const stage = stages.find((s) => s.key === loan.stage_key);
  const today = todayIn(me.settings.timezone);
  const ro = !loan.can_edit;
  const set = (s: LoanSet) => void actions.patch(loan, s);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const redAmber = loan.alerts.filter((a) => a.severity !== "info").length;

  const buildLosUrl = (overrideStage?: string | null, overrideTab?: string) => {
    const q = new URLSearchParams();
    q.set("loan", loan.loan_number || loan.id);
    if (overrideStage ?? loan.stage_key) q.set("stage", (overrideStage ?? loan.stage_key)!);
    if (overrideTab) q.set("tab", overrideTab);
    if (loan.file_name) q.set("name", loan.file_name);
    if (loan.amount) q.set("amount", loan.amount);
    if (loan.note_rate) q.set("rate", loan.note_rate);
    if (loan.broker) q.set("broker", loan.broker);
    return `/los?${q.toString()}`;
  };

  return (
    <>
      <header className="border-b border-subtle px-5 pt-4 pb-3">
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="eyebrow text-ink-3">
              {loan.owner_name}
              {ro ? " · read-only" : ""}
            </div>
            <InlineText
              ariaLabel="File name"
              className="font-display w-full truncate text-[20px] leading-7 font-semibold"
              value={loan.file_name}
              placeholder="Untitled loan"
              disabled={ro}
              onCommit={(v) => set({ file_name: v })}
              status={cellStatus(loan.id, "file_name")?.status}
            />
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[13px] text-ink-2 tnum">
              <span className="loan-no">{loan.loan_number || "No loan #"}</span>
              <span aria-hidden>·</span>
              <span>{loan.amount ? money(loan.amount) : "No amount"}</span>
              {loan.loan_type ? (
                <>
                  <span aria-hidden>·</span>
                  <span>{loan.loan_type}</span>
                </>
              ) : null}
            </div>
          </div>
          <button data-autofocus="" onClick={onClose} className="focus-ring rounded-md p-1.5 text-ink-2 hover:bg-hover hover:text-ink" aria-label="Close loan drawer (Esc)">
            <X size={18} />
          </button>
        </div>
        <PresenceAvatars loanId={loan.id} userId={me.user.id} />
        <Stepper loan={loan} me={me} onPick={(k) => set({ stage_key: k })} />
        <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <TierChip forecast={loan.forecast} />
            <StagePill stage={stage} total={stages.length} />
            {loan.outcome !== "active" ? (
              <span className="rounded-full bg-sunken px-2 py-0.5 text-[12px] font-medium text-ink-2">{OUTCOME_LABEL[loan.outcome]}</span>
            ) : null}
          </div>
          <Button
            size="sm"
            variant="primary"
            onClick={() => {
              onClose();
              navigate(buildLosUrl());
            }}
          >
            <ExternalLink size={13} /> Open in LSAI LOS
          </Button>
        </div>
      </header>

      <Tabs.Root defaultValue="details" className="flex min-h-0 flex-1 flex-col">
        <Tabs.List aria-label="Loan sections" className="flex gap-1 border-b border-subtle px-4">
          {[
            ["details", "Details"],
            ["alerts", `Alerts${redAmber ? ` (${redAmber})` : ""}`],
            ["history", "History"],
          ].map(([v, l]) => (
            <Tabs.Trigger
              key={v}
              value={v}
              className="focus-ring -mb-px border-b-2 border-transparent px-3 py-2 text-[13px] font-medium text-ink-2 hover:text-ink data-[state=active]:border-primary data-[state=active]:text-ink"
            >
              {l}
            </Tabs.Trigger>
          ))}
        </Tabs.List>

        <Tabs.Content value="details" className="min-h-0 flex-1 overflow-y-auto px-5 py-4 outline-none">
          <div className="mb-4 rounded-lg border border-line bg-sunken p-3 text-[12px]">
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-ink">
                LSAI LOS Unified Handoff (CA Pre-Qual → Disclosed → AM Underwriting)
              </span>
              <span className="rounded bg-primary/15 px-1.5 py-0.5 font-mono text-[11px] font-semibold text-ink">
                {stage?.label ?? "Pre-Qual"}
              </span>
            </div>
            <p className="mt-1 text-ink-2">
              {loan.stage_key === "pre_approved" || !loan.stage_key
                ? "Stage 1 (CA Pre-Qual): Credit Analyst structures 1003 & DSCR. Generate & send disclosures via LoanShip Native e-Sign to advance into Underwriting with the AM (Account Manager / Jr. UW)."
                : loan.stage_key === "le_requested" || loan.stage_key === "le_sent" || loan.stage_key === "le_signed"
                  ? "Stage 2 (Disclosed): Disclosures generated & sent via LoanShip Native e-Sign. Ready for Underwriting with the AM (Account Manager — Processor / Jr. Underwriter) to clear conditions with the Broker."
                  : loan.stage_key === "submitted_to_uw" || loan.stage_key === "cla_issued"
                    ? "Stage 3 (AM Underwriting): Assigned to the AM (Account Manager — Processor / Jr. Underwriter) to collect and clear conditions with the Broker."
                    : "Stage 4 (Closing & Funding): Clear to Close, LoanShip Native e-Sign Closing Package & Wire Release in LSAI LOS."}
            </p>
            <div className="mt-2.5 flex flex-wrap gap-2">
              {(loan.stage_key === "pre_approved" || !loan.stage_key) && !ro ? (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    set({ stage_key: "le_sent" });
                    onClose();
                    navigate(buildLosUrl("le_sent", "closing"));
                  }}
                >
                  Generate &amp; Send Disclosures (LoanShip Native e-Sign) →
                </Button>
              ) : null}
              {(loan.stage_key === "le_requested" || loan.stage_key === "le_sent" || loan.stage_key === "le_signed") && !ro ? (
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => {
                    set({ stage_key: "submitted_to_uw" });
                    onClose();
                    navigate(buildLosUrl("submitted_to_uw", "milestones"));
                  }}
                >
                  Move to AM Underwriting (Clear Conditions w/ Broker) →
                </Button>
              ) : null}
              <Button
                size="sm"
                onClick={() => {
                  onClose();
                  navigate(
                    buildLosUrl(
                      loan.stage_key,
                      loan.stage_key === "submitted_to_uw" || loan.stage_key === "cla_issued"
                        ? "milestones"
                        : loan.stage_key === "le_sent" || loan.stage_key === "cd_sent" || loan.stage_key === "ctc_docs_out"
                          ? "closing"
                          : "terms",
                    ),
                  );
                }}
              >
                <ExternalLink size={13} />{" "}
                {loan.stage_key === "submitted_to_uw" || loan.stage_key === "cla_issued"
                  ? "Clear Broker Conditions as AM in LSAI LOS"
                  : "Open Workbench in LSAI LOS"}
              </Button>
            </div>
          </div>

          <RevenueExplainer loan={loan} target={Number(me.settings.price_target) || 103} onApply={(p) => set({ price: p })} />

          <Section title="Money">
            <Field loan={loan} field="price" label="Price" kind="price" />
            <Field loan={loan} field="amount" label="Amount" kind="money" />
            <Field loan={loan} field="note_rate" label="Note rate %" kind="text" />
            {loan.comp ? (
              <div className="col-span-2 rounded-md bg-sunken px-3 py-2 text-[13px] text-ink-2 tnum">
                Comp ({loan.comp.program}): {loan.comp.bps ?? "—"} bps
                {loan.comp.commission ? (
                  <>
                    {" "}
                    = <b className="text-ink">{money(loan.comp.commission)}</b>
                  </>
                ) : (
                  " · needs price"
                )}
              </div>
            ) : null}
          </Section>

          {previewOn(me) ? (
            <Section title="Loan steps · Preview" cols={1}>
              <LoanStepTracker stageKey={loan.stage_key} stages={stages} />
            </Section>
          ) : null}

          <Section title="Pipeline">
            <SelectField
              loan={loan}
              field="forecast"
              label="Tier"
              options={[{ value: "", label: "—" }, ...["PIN", "MONEYBALL", "HIGH", "TBD"].map((v) => ({ value: v, label: FORECAST_LONG[v] }))]}
            />
            <SelectField
              loan={loan}
              field="stage_key"
              label="Stage"
              options={[{ value: "", label: "—" }, ...stages.map((s) => ({ value: s.key, label: `${s.sort}. ${s.label}` }))]}
            />
            <SelectField
              loan={loan}
              field="outcome"
              label="Outcome"
              options={Object.entries(OUTCOME_LABEL).map(([value, label]) => ({ value, label }))}
              nullable={false}
            />
            <OwnerField loan={loan} me={me} />
            <Field loan={loan} field="loan_number" label="Loan #" kind="text" />
            <Field loan={loan} field="loan_type" label="Loan type" kind="text" />
            <Field loan={loan} field="broker" label="Broker / LO" kind="text" wide />
          </Section>

          <Section title="Dates">
            <Field loan={loan} field="coe_date" label="COE" kind="date" hint={dateHint(loan.coe_date, today)} />
            <Field loan={loan} field="target_month" label="Count in month" kind="month" hint={loan.reporting_month ? `Reporting: ${monthLabel(loan.reporting_month)}` : undefined} />
            <SelectField
              loan={loan}
              field="lock_status"
              label="Lock"
              options={[{ value: "", label: "—" }, ...Object.entries(LOCK_LABEL).map(([value, label]) => ({ value, label }))]}
            />
            <Field loan={loan} field="lock_expires_on" label="Lock expires" kind="date" hint={dateHint(loan.lock_expires_on, today)} />
            <Field loan={loan} field="last_cd_date" label="Last CD" kind="date" />
            <Field loan={loan} field="signing_date" label="Signing" kind="date" />
            <Field loan={loan} field="funded_date" label="Funded" kind="date" />
          </Section>

          <Section title="Checklist">
            <Field loan={loan} field="appraisal" label="Appraisal" kind="text" />
            <BoolField loan={loan} field="hoi_ok" label="HOI" />
            <BoolField loan={loan} field="title_ok" label="Title" />
            <BoolField loan={loan} field="form_4506_ok" label="4506-C" />
          </Section>

          <Section title="Notes" cols={1}>
            <NotesField loan={loan} />
          </Section>

          {hasCap(me, "view_conditions") && <Section title="Conditions" cols={1}>
            <Link to={`/conditions/loans/${encodeURIComponent(loan.id)}`} className="focus-ring text-link underline">Review conditions</Link>
          </Section>}

          <Section title="Self-Employed Borrower Questionnaire" cols={1}>
            <Link
              to={`/loans/${encodeURIComponent(loan.id)}/seq`}
              className="focus-ring inline-flex h-8 w-fit items-center gap-1.5 rounded-md border border-line bg-surface px-2.5 text-[13px] font-medium text-ink hover:bg-hover"
            >
              <ClipboardList size={14} /> Open SEQ
            </Link>
          </Section>

          <Section title="Core loan workspace" cols={1}>
            <div className="flex flex-wrap gap-3">
              <Link to={`/loans/${encodeURIComponent(loan.id)}/workspace`} className="focus-ring text-link underline">Open Core workspace</Link>
              <Link to={`/documents/${encodeURIComponent(loan.id)}`} className="focus-ring text-link underline">Documents</Link>
            </div>
          </Section>

          <Section title="Credit report" cols={1}>
            <Link to={`/loans/${encodeURIComponent(loan.id)}/credit-ocr`} className="focus-ring text-link underline">Review credit OCR availability</Link>
          </Section>

          <Section title="Smart Document Drop & Triage" cols={1}>
            <LoanSmartDropPanel
              loanId={loan.id}
              loanNumber={loan.loan_number}
              borrowerName={loan.file_name}
              loName={loan.broker}
            />
          </Section>

          <LoCardSection loanId={loan.id} />

          <AssignedStaff loanId={loan.id} me={me} />

          <footer className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-subtle pt-3 text-[12px] text-ink-3">
            <span>
              Updated {new Date(loan.updated_at).toLocaleString()} {loan.updated_by_name ? `by ${loan.updated_by_name}` : ""}
            </span>
            {loan.can_delete ? (
              confirmDelete ? (
                <span className="flex items-center gap-2">
                  <span className="text-ink-2">Delete this loan?</span>
                  <Button size="sm" onClick={() => setConfirmDelete(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={async () => {
                      if (await actions.remove(loan)) onClose();
                    }}
                  >
                    Delete
                  </Button>
                </span>
              ) : (
                <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
                  <Trash2 size={14} /> Delete
                </Button>
              )
            ) : null}
          </footer>
        </Tabs.Content>

        <Tabs.Content value="alerts" className="min-h-0 flex-1 overflow-y-auto px-5 py-4 outline-none">
          {loan.alerts.length === 0 ? (
            <div className="flex items-center gap-2 rounded-md bg-success-bg px-3 py-3 text-success">
              <Check size={16} /> All clear: no lock, date or data problems on this loan.
            </div>
          ) : (
            <ul className="space-y-2">
              {loan.alerts.map((a) => (
                <li
                  key={a.code}
                  className={cx(
                    "flex items-start gap-2 rounded-md px-3 py-2.5",
                    a.severity === "red" ? "bg-danger-bg text-danger" : a.severity === "amber" ? "bg-warning-bg text-warning" : "bg-info-bg text-info",
                  )}
                >
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden />
                  <div>
                    <div className="font-medium">{a.message}</div>
                    <div className="text-[12px] opacity-80">{a.severity === "red" ? "Needs action now" : a.severity === "amber" ? "Needs attention soon" : "For information"}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Tabs.Content>

        <Tabs.Content value="history" className="min-h-0 flex-1 overflow-y-auto px-5 py-4 outline-none">
          <HistoryList loan={loan} me={me} />
        </Tabs.Content>
      </Tabs.Root>
    </>
  );
}

function dateHint(d: string | null, today: string): string | undefined {
  if (!d) return undefined;
  const n = daysBetween(today, d);
  return `${dateLong(d)} · ${n === 0 ? "today" : n > 0 ? `in ${n}d` : `${-n}d ago`}`;
}

function Stepper({ loan, me, onPick }: { loan: Loan; me: Me; onPick: (key: string) => void }) {
  const dark = useIsDark();
  const cur = me.stages.find((s) => s.key === loan.stage_key)?.sort ?? 0;
  return (
    <div className="mt-3 flex items-center gap-[3px]" role="group" aria-label="Stage">
      {me.stages.map((s) => {
        const tone = stageTone(s, dark)!;
        const done = s.sort <= cur;
        return (
          <button
            key={s.key}
            disabled={!loan.can_edit}
            onClick={() => onPick(s.key)}
            title={`${s.sort}. ${s.label}`}
            aria-label={`Set stage ${s.label}`}
            aria-pressed={s.sort === cur}
            className={cx(
              "focus-ring h-2.5 flex-1 rounded-full transition-[transform] enabled:hover:scale-y-150 disabled:cursor-default",
              s.sort === cur && "ring-2 ring-ink/50 ring-offset-1 ring-offset-raised",
            )}
            style={{ background: done ? tone.solid : "var(--bg-sunken)" }}
          />
        );
      })}
    </div>
  );
}

function RevenueExplainer({ loan, target, onApply }: { loan: Loan; target: number; onApply: (price: string) => void }) {
  const p = num(loan.price);
  const a = num(loan.amount);
  const [whatIf, setWhatIf] = useState<number>(p !== null ? Math.max(p, target) : target);
  useEffect(() => {
    if (p !== null) setWhatIf((w) => (w === p ? w : Math.max(p, target)));
  }, [p, target]);
  const rev = revenue(a, p);
  const rev2 = revenue(a, whatIf);
  const delta = rev !== null && rev2 !== null ? rev2 - rev : null;
  return (
    <div className="rounded-[10px] border border-subtle bg-surface p-4 shadow-[var(--shadow-1)]">
      <div className="eyebrow mb-1 text-ink-3">Revenue on this loan</div>
      {p !== null ? (
        <>
          <div className="flex flex-wrap items-baseline gap-x-2 text-[18px] font-semibold tnum">
            <span>{price3(p)}</span>
            <span className="text-ink-3">→</span>
            <span>{pts3(p - 100)} pts</span>
            <span className="text-ink-3">→</span>
            <span className={cx(p >= target ? "text-accent-text" : "text-ink")}>{rev !== null ? money(rev) : "add amount"}</span>
          </div>
          {a !== null ? (
            <div className="mt-0.5 text-[13px] text-ink-2 tnum">
              {money(a)} × {pts3(p - 100)}% = {money(rev)}
            </div>
          ) : null}
          <div className={cx("mt-1 text-[13px] tnum", p >= target ? "text-success" : p < 100 ? "text-danger" : "text-warning")}>
            {p >= target
              ? `● At or above the ${target.toFixed(3)} target`
              : p < 100
                ? "Below par: this loan loses money"
                : `◐ ${(target - p).toFixed(3)} below ${target.toFixed(3)} target${a !== null ? ` · at target it earns ${money(revenue(a, target))}` : ""}`}
          </div>
        </>
      ) : (
        <div className="text-ink-2">Add a price to see revenue. Type 102.75, 2.75 (points) or 1.0275.</div>
      )}
      {a !== null ? (
        <div className="mt-3 border-t border-dashed border-line pt-3">
          <label className="flex items-center gap-2 text-[13px] text-ink-2" htmlFor={`whatif-${loan.id}`}>
            What if priced at
            <input
              type="number"
              step={0.125}
              min={95}
              max={110}
              value={whatIf}
              onChange={(e) => setWhatIf(Number(e.target.value))}
              className="focus-ring w-24 rounded-sm border border-dashed border-navy-500 bg-surface px-2 py-1 text-right text-ink tnum"
              aria-label="What-if price"
            />
          </label>
          <input
            id={`whatif-${loan.id}`}
            type="range"
            min={100}
            max={106}
            step={0.125}
            value={whatIf}
            onChange={(e) => setWhatIf(Number(e.target.value))}
            className="mt-2 w-full accent-[var(--primary)]"
            aria-label="What-if price slider"
          />
          <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-[13px] tnum">
            <span>
              {price3(whatIf)} → <b>{money(rev2)}</b>
              {delta !== null ? (
                <span className={cx("ml-1.5 font-semibold", delta > 0 ? "text-success" : delta < 0 ? "text-danger" : "text-ink-3")}>
                  ({signedMoney(Math.round(delta))})
                </span>
              ) : null}
            </span>
            {loan.can_edit && whatIf !== p ? (
              <Button size="sm" onClick={() => onApply(whatIf.toFixed(3))}>
                Apply {price3(whatIf)}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Section({ title, children, cols = 2 }: { title: string; children: ReactNode; cols?: 1 | 2 }) {
  return (
    <section className="mt-5">
      <h3 className="eyebrow mb-2 border-b border-subtle pb-1 text-ink-3">{title}</h3>
      <div className={cx("grid gap-x-4 gap-y-3", cols === 2 ? "grid-cols-2" : "grid-cols-1")}>{children}</div>
    </section>
  );
}

function SaveDot({ loanId, field }: { loanId: string; field: string }) {
  useSaveState();
  const st = cellStatus(loanId, field);
  if (!st) return null;
  if (st.status === "saving") return <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-ink-3" aria-label="Saving" />;
  if (st.status === "saved") return <Check size={12} className="text-success" aria-label="Saved" />;
  return (
    <span className="text-danger" title={st.message} aria-label={`Not saved: ${st.message}`}>
      <AlertTriangle size={12} />
    </span>
  );
}

function FieldShell({ loan, field, label, children, hint, wide, error }: { loan: Loan; field: string; label: string; children: ReactNode; hint?: string; wide?: boolean; error?: string }) {
  return (
    <div data-presence-field={field} className={cx("min-w-0", wide && "col-span-2")}>
      <div className="mb-1 flex items-center gap-1.5 text-[12px] font-medium text-ink-2">
        <label htmlFor={`f-${loan.id}-${field}`}>{label}</label>
        <SaveDot loanId={loan.id} field={field} />
      </div>
      {children}
      {error ? (
        <div className="mt-0.5 text-[12px] text-danger" id={`f-${loan.id}-${field}-err`}>
          {error}
        </div>
      ) : hint ? (
        <div className="mt-0.5 truncate text-[12px] text-ink-3 tnum">{hint}</div>
      ) : null}
    </div>
  );
}

const inputCls =
  "focus-ring h-9 w-full rounded-sm border border-input bg-surface px-2.5 text-[14px] text-ink placeholder:text-ink-3 disabled:border-subtle disabled:bg-sunken disabled:text-ink-2";

function rawOf(loan: Loan, field: EditableField, kind: string): string {
  const v = loan[field as keyof Loan] as string | null;
  if (v === null || v === undefined) return "";
  if (kind === "price") return price3(v);
  if (kind === "money") return String(num(v));
  if (kind === "month") return v.slice(0, 7);
  return String(v);
}

function Field({ loan, field, label, kind, hint, wide }: { loan: Loan; field: EditableField; label: string; kind: "text" | "money" | "price" | "date" | "month"; hint?: string; wide?: boolean }) {
  const actions = useLoanActions();
  const server = rawOf(loan, field, kind);
  const [draft, setDraft] = useState(server);
  const [focused, setFocused] = useState(false);
  const [err, setErr] = useState<string | undefined>();
  useEffect(() => {
    if (!focused) setDraft(server);
  }, [server, focused]);
  const commit = () => {
    if (draft === server) return setErr(undefined);
    let value: FieldValue = draft.trim() === "" ? null : draft.trim();
    if (kind === "money") {
      const a = parseAmount(draft);
      if (!a.ok) return setErr(a.message);
      value = a.value;
    }
    if (kind === "month" && value) value = `${value}-01`;
    setErr(undefined);
    void actions.patch(loan, { [field]: value });
  };
  let preview: string | undefined;
  if (kind === "price" && focused && draft.trim() && draft !== server) {
    const p = previewPrice(draft);
    preview = p.ok ? `${draft.trim()} → ${price3(p.price)} · ${pts3(p.price - 100)} pts${loan.amount ? ` · ${money(revenue(loan.amount, p.price))}` : ""}` : p.message;
  }
  const st = cellStatus(loan.id, field);
  return (
    <FieldShell loan={loan} field={field} label={label} hint={preview ?? hint} wide={wide} error={err ?? (st?.status === "error" ? st.message : undefined)}>
      <input
        id={`f-${loan.id}-${field}`}
        type={kind === "date" ? "date" : kind === "month" ? "month" : "text"}
        inputMode={kind === "money" || kind === "price" ? "decimal" : undefined}
        className={cx(inputCls, (kind === "money" || kind === "price") && "text-right tnum", err && "border-danger")}
        value={draft}
        disabled={!loan.can_edit}
        aria-invalid={!!err}
        aria-describedby={err ? `f-${loan.id}-${field}-err` : undefined}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape") {
            e.preventDefault();
            setDraft(server);
            setErr(undefined);
          }
        }}
      />
    </FieldShell>
  );
}

function SelectField({ loan, field, label, options, nullable = true }: { loan: Loan; field: EditableField; label: string; options: { value: string; label: string }[]; nullable?: boolean }) {
  const actions = useLoanActions();
  const v = (loan[field as keyof Loan] as string | null) ?? "";
  return (
    <FieldShell loan={loan} field={field} label={label}>
      <select
        id={`f-${loan.id}-${field}`}
        className={inputCls}
        value={v}
        disabled={!loan.can_edit}
        onChange={(e) => void actions.patch(loan, { [field]: e.target.value === "" && nullable ? null : e.target.value })}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

function BoolField({ loan, field, label }: { loan: Loan; field: EditableField; label: string }) {
  const actions = useLoanActions();
  const v = loan[field as keyof Loan] as boolean | null;
  return (
    <FieldShell loan={loan} field={field} label={label}>
      <select
        id={`f-${loan.id}-${field}`}
        className={inputCls}
        value={v === null ? "" : v ? "yes" : "no"}
        disabled={!loan.can_edit}
        onChange={(e) => void actions.patch(loan, { [field]: e.target.value === "" ? null : e.target.value === "yes" })}
      >
        <option value="">—</option>
        <option value="yes">OK</option>
        <option value="no">Not yet</option>
      </select>
    </FieldShell>
  );
}

function OwnerField({ loan, me }: { loan: Loan; me: Me }) {
  const actions = useLoanActions();
  const users = useUsers();
  const canChange = loan.can_edit && hasCap(me, "reassign_loans");
  if (!canChange) {
    return (
      <FieldShell loan={loan} field="owner_user_id" label="Owner (AE)">
        <div className="flex h-9 items-center text-[14px] text-ink">{loan.owner_name}</div>
      </FieldShell>
    );
  }
  const options = (users.data ?? []).filter((u) => (u.role === "ae" && u.status === "active") || u.id === loan.owner_user_id || u.id === me.user.id);
  return (
    <FieldShell loan={loan} field="owner_user_id" label="Owner (AE)">
      <select
        id={`f-${loan.id}-owner_user_id`}
        className={inputCls}
        value={loan.owner_user_id}
        onChange={(e) => void actions.patch(loan, { owner_user_id: e.target.value })}
      >
        {options.length === 0 ? <option value={loan.owner_user_id}>{loan.owner_name}</option> : null}
        {options.map((u) => (
          <option key={u.id} value={u.id}>
            {u.display_name}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

function NotesField({ loan }: { loan: Loan }) {
  const actions = useLoanActions();
  const server = loan.notes ?? "";
  const [draft, setDraft] = useState(server);
  const [focused, setFocused] = useState(false);
  const timer = useRef<number | undefined>(undefined);
  const latest = useRef({ loan, draft });
  latest.current = { loan, draft };
  useEffect(() => {
    if (!focused) setDraft(server);
  }, [server, focused]);
  const commit = () => {
    window.clearTimeout(timer.current);
    const { loan: l, draft: d } = latest.current;
    if (d !== (l.notes ?? "")) void actions.patch(l, { notes: d.trim() === "" ? null : d });
  };
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return (
    <FieldShell loan={loan} field="notes" label="Notes (saves as you pause)" wide>
      <textarea
        id={`f-${loan.id}-notes`}
        rows={5}
        className={cx(inputCls, "h-auto py-2 leading-5")}
        value={draft}
        disabled={!loan.can_edit}
        placeholder="Next step, who's waiting on what…"
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          commit();
        }}
        onChange={(e) => {
          setDraft(e.target.value);
          window.clearTimeout(timer.current);
          timer.current = window.setTimeout(commit, 800);
        }}
      />
    </FieldShell>
  );
}

function InlineText(props: {
  value: string;
  onCommit: (v: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  ariaLabel: string;
  autoFocus?: boolean;
  status?: string;
}) {
  const [draft, setDraft] = useState(props.value);
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (!focused) setDraft(props.value);
  }, [props.value, focused]);
  return (
    <input
      data-presence-field={props.ariaLabel === "File name" ? "file_name" : undefined}
      data-autofocus={props.autoFocus ? "" : undefined}
      aria-label={props.ariaLabel}
      className={cx("focus-ring -mx-1 rounded-sm border border-transparent bg-transparent px-1 text-ink hover:border-subtle focus:border-input disabled:hover:border-transparent", props.className)}
      value={draft}
      placeholder={props.placeholder}
      disabled={props.disabled}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        const v = draft.replace(/\s+/g, " ").trim();
        if (v !== props.value) props.onCommit(v);
      }}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") (e.target as HTMLInputElement).blur();
      }}
    />
  );
}

// ── History ──────────────────────────────────────────────────────────────────

function fmtHistoryValue(field: string, v: unknown, me: Me, today: string): string {
  if (v === null || v === undefined || v === "") return "—";
  if (typeof v === "boolean") return v ? "OK" : "Not yet";
  if (field === "created") return "Loan created";
  const s = String(v);
  if (field === "price") return price3(s);
  if (field === "amount") return money(s);
  if (field === "stage_key") return me.stages.find((x) => x.key === s)?.label ?? s;
  if (field === "forecast") return FORECAST_LONG[s] ?? s;
  if (field === "lock_status") return LOCK_LABEL[s] ?? s;
  if (field === "outcome") return OUTCOME_LABEL[s] ?? s;
  if (field === "target_month") return monthLabel(s.slice(0, 10), true);
  if (field.endsWith("_date") || field === "lock_expires_on") return dateShort(s.slice(0, 10), today);
  if (field === "deleted_at") return "deleted";
  return s.length > 80 ? `${s.slice(0, 80)}…` : s;
}

function HistoryList({ loan, me }: { loan: Loan; me: Me }) {
  const q = useHistory(loan.id);
  const today = todayIn(me.settings.timezone);
  if (q.isLoading) return <div className="text-ink-3">Loading history…</div>;
  if (q.isError) return <div className="text-danger">Couldn't load history. {(q.error as Error).message}</div>;
  const rows = (q.data ?? []).filter((h) => !["revision", "updated_at", "updated_by"].includes(h.field));
  if (!rows.length)
    return (
      <div className="flex items-center gap-2 text-ink-2">
        <History size={16} /> No changes recorded yet.
      </div>
    );
  const groups = new Map<string, HistoryEntry[]>();
  for (const h of rows) {
    const d = new Date(h.at);
    const key = d.toDateString();
    groups.set(key, [...(groups.get(key) ?? []), h]);
  }
  const todayKey = new Date().toDateString();
  return (
    <div className="space-y-4">
      {[...groups.entries()].map(([day, items]) => (
        <section key={day}>
          <h4 className="eyebrow mb-1.5 text-ink-3">{day === todayKey ? "Today" : day}</h4>
          <ol className="space-y-1">
            {items.map((h) => (
              <li key={h.id} className="grid grid-cols-[64px_1fr] gap-2 rounded-md px-1 py-1 text-[13px] hover:bg-hover">
                <time className="whitespace-nowrap text-ink-3 tnum" dateTime={h.at}>
                  {new Date(h.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </time>
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-1.5">
                    <span className="font-medium text-ink">{h.actor_name ?? "System"}</span>
                    <span className="text-ink-2">{fieldLabel(h.field)}</span>
                    {h.source && h.source !== "ui" ? (
                      <span className="rounded-full bg-sunken px-1.5 text-[11px] font-medium text-ink-2">{h.source}</span>
                    ) : null}
                  </div>
                  <div className="truncate text-ink-2 tnum">
                    <span className="line-through decoration-ink-3/60">{fmtHistoryValue(h.field, h.old_value, me, today)}</span>
                    <span className="mx-1.5 text-ink-3">→</span>
                    <span className="text-ink">{fmtHistoryValue(h.field, h.new_value, me, today)}</span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}
